import React, { useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import ReactMarkdown from 'react-markdown'
import Box from '@material-ui/core/Box'
import Collapse from '@material-ui/core/Collapse'
import Divider from '@material-ui/core/Divider'
import Typography from '@material-ui/core/Typography'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import ExpandLessIcon from 'components/material-symbol/icons/ExpandLess'
import ExpandMoreIcon from 'components/material-symbol/icons/ExpandMore'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import VideocamIcon from 'components/material-symbol/icons/Videocam'
import { centsToDollarsFormatted } from 'lib/utils/number'
import CoachLine from './coach-line'
import StepsList from './steps-list'
import { useStyles } from './styles'
import { formatTurnaroundWithin, MAX_VIDEOS } from './utils'

export const CHECKOUT_STEPS = [
  { title: 'Complete your payment' },
  { title: `Upload your swing videos (up to ${MAX_VIDEOS})` },
  { title: 'Receive your personalized analysis' },
]

const BODY_ID = 'order-summary-body'

const OrderSummary = ({ offering }) => {
  const classes = useStyles()
  // Below md the checkout grid stacks, so the summary sits above the payment
  // panel; collapse it there. noSsr reads the real width on first render so
  // the card does not animate shut after mounting.
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), {
    noSsr: true,
  })
  const [expanded, setExpanded] = useState(false)
  const turnaround = formatTurnaroundWithin(
    offering.virtualCoaching?.turnaroundHours,
  )
  const total = centsToDollarsFormatted(offering.priceInCents) || '$0.00'
  const ChevronIcon = expanded ? ExpandLessIcon : ExpandMoreIcon

  return (
    <section className={classes.card} aria-labelledby="order-summary-title">
      <Box className={classes.cardHeader}>
        {isMobile ? (
          <h2 className={classes.orderSummaryHeading}>
            <button
              type="button"
              className={clsx(
                classes.orderSummaryToggle,
                !expanded && classes.orderSummaryToggleCollapsed,
              )}
              aria-expanded={expanded}
              aria-controls={BODY_ID}
              onClick={() => setExpanded(open => !open)}
            >
              <span className={classes.orderSummaryToggleText}>
                <Typography
                  id="order-summary-title"
                  component="span"
                  display="block"
                  className={classes.cardTitleText}
                >
                  Order summary
                </Typography>
                <Typography
                  component="span"
                  display="block"
                  className={classes.cardSubtitle}
                >
                  Review your selection
                </Typography>
                {expanded ? null : (
                  <span className={classes.orderSummaryCompact}>
                    {offering.title} · {total}
                  </span>
                )}
              </span>
              <span className={classes.orderSummaryChevron} aria-hidden="true">
                <ChevronIcon />
              </span>
            </button>
          </h2>
        ) : (
          <Box className={classes.cardTitleBlock}>
            <Typography
              id="order-summary-title"
              variant="h2"
              className={classes.cardTitleText}
            >
              Order summary
            </Typography>
            <Typography className={classes.cardSubtitle}>
              Review your selection
            </Typography>
          </Box>
        )}
      </Box>
      <Collapse
        id={BODY_ID}
        in={!isMobile || expanded}
        timeout={isMobile ? 200 : 0}
      >
        <Box className={classes.cardBody}>
          <Box className={classes.offeringRow}>
            <Box className={classes.iconTile} aria-hidden="true">
              <VideocamIcon />
            </Box>
            <Box minWidth={0}>
              <Typography
                variant="h3"
                component="p"
                className={classes.offeringTitle}
              >
                {offering.title}
              </Typography>
              {turnaround ? (
                <Box className={classes.metaRow}>
                  <ScheduleIcon aria-hidden="true" />
                  <span>Turnaround {turnaround}</span>
                </Box>
              ) : null}
            </Box>
          </Box>

          <CoachLine coach={offering.coach} variant="card" />

          {offering.description ? (
            <>
              <Divider className={classes.divider} />
              <Typography
                variant="h3"
                component="h3"
                className={classes.sectionLabel}
              >
                What&apos;s included
              </Typography>
              <Box
                className={classes.markdown}
                data-testid="offering-description"
              >
                <ReactMarkdown>{offering.description}</ReactMarkdown>
              </Box>
            </>
          ) : null}

          <Divider className={classes.divider} />
          <Typography
            variant="h3"
            component="h3"
            className={classes.sectionLabel}
          >
            Next steps
          </Typography>
          <StepsList steps={CHECKOUT_STEPS} aria-label="Next steps" />

          <Divider className={classes.divider} />
          <Box className={classes.totalRow}>
            <span>Total</span>
            <span data-testid="order-total">{total}</span>
          </Box>
        </Box>
      </Collapse>
    </section>
  )
}

OrderSummary.propTypes = {
  offering: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    priceInCents: PropTypes.number,
    virtualCoaching: PropTypes.shape({ turnaroundHours: PropTypes.number }),
    coach: PropTypes.object,
  }).isRequired,
}

export default OrderSummary
